import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/views/SetupView.vue'),
      meta: { public: true },
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: () => import('@/views/InviteView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('@/views/TasksView.vue'),
    },
    {
      path: '/tasks/:id',
      name: 'task-detail',
      component: () => import('@/views/TaskDetailView.vue'),
      props: true,
    },
    {
      path: '/tasks/:id/edit',
      name: 'task-editor',
      component: () => import('@/views/TaskEditorView.vue'),
      props: true,
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/tasks/new',
      name: 'task-new',
      component: () => import('@/views/TaskEditorView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/runs',
      name: 'runs',
      component: () => import('@/views/RunsView.vue'),
    },
    {
      path: '/runs/:id',
      name: 'run-detail',
      component: () => import('@/views/RunDetailView.vue'),
      props: true,
    },
    {
      path: '/connectors',
      name: 'connectors',
      component: () => import('@/views/ConnectorsView.vue'),
    },
    {
      path: '/connectors/connect-callback',
      name: 'connectors-connect-callback',
      component: () => import('@/views/ConnectCallbackView.vue'),
    },
    {
      path: '/data-explorer/:connectorId?',
      name: 'data-explorer',
      component: () => import('@/views/DataExplorerView.vue'),
      props: true,
      meta: { fullWidth: true },
    },
    {
      path: '/triggers',
      name: 'triggers',
      component: () => import('@/views/TriggersView.vue'),
    },
    {
      path: '/job-templates',
      name: 'job-templates',
      component: () => import('@/views/JobTemplatesView.vue'),
    },
    {
      path: '/job-templates/new',
      name: 'job-template-new',
      component: () => import('@/views/JobTemplateEditorView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/job-templates/:id/edit',
      name: 'job-template-editor',
      component: () => import('@/views/JobTemplateEditorView.vue'),
      props: true,
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/job-templates/:id',
      name: 'job-template-detail',
      component: () => import('@/views/JobTemplateDetailView.vue'),
      props: true,
    },
    {
      path: '/job-templates/:templateId/dashboard-templates/new',
      name: 'dashboard-template-new',
      component: () => import('@/views/DashboardTemplateEditorView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/job-templates/:templateId/dashboard-templates/:dashboardTemplateId/edit',
      name: 'dashboard-template-editor',
      component: () => import('@/views/DashboardTemplateEditorView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: () => import('@/views/JobsView.vue'),
    },
    {
      path: '/jobs/:id',
      name: 'job-detail',
      component: () => import('@/views/JobDetailView.vue'),
      props: true,
    },
    {
      path: '/jobs/:id/tasks/:taskKey/edit',
      name: 'job-task-editor',
      component: () => import('@/views/JobTaskEditorView.vue'),
      props: true,
    },
    {
      path: '/dashboards/:id',
      name: 'dashboard-detail',
      component: () => import('@/views/DashboardDetailView.vue'),
      props: true,
    },
    {
      path: '/dashboards/:id/edit',
      name: 'dashboard-editor',
      component: () => import('@/views/DashboardEditorView.vue'),
      props: true,
    },
    {
      path: '/public/dashboards/:token',
      name: 'public-dashboard',
      component: () => import('@/views/PublicDashboardView.vue'),
      meta: { public: true },
    },
    {
      path: '/oauth/consent',
      name: 'oauth-consent',
      component: () => import('@/views/OAuthConsentView.vue'),
      // Auth required (not public), but hide app chrome — consent is a standalone interstitial.
      meta: { bare: true },
    },
    {
      path: '/job-runs/:id',
      name: 'job-run-detail',
      component: () => import('@/views/JobRunDetailView.vue'),
      props: true,
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/executors',
      name: 'executors',
      component: () => import('@/views/ExecutorsView.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
    },
    {
      path: '/ai-references',
      name: 'ai-references',
      component: () => import('@/views/AiReferencesView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/ai-references/new',
      name: 'ai-reference-new',
      component: () => import('@/views/AiReferenceEditorView.vue'),
      meta: { roles: ['superadmin', 'admin'] },
    },
    {
      path: '/ai-references/:id/edit',
      name: 'ai-reference-editor',
      component: () => import('@/views/AiReferenceEditorView.vue'),
      props: true,
      meta: { roles: ['superadmin', 'admin'] },
    },
  ],
})

let statusChecked = false

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!statusChecked) {
    const branding = useBrandingStore()
    await Promise.all([auth.checkStatus(), branding.fetch()])
    statusChecked = true
  }

  if (auth.needsSetup) {
    if (to.name !== 'setup' && to.name !== 'invite') return { name: 'setup' }
    return
  }

  // Setup is done — don't allow visiting /setup again
  if (to.name === 'setup') {
    return { name: 'dashboard' }
  }

  if (!auth.isAuthenticated && !to.meta.public) {
    await auth.fetchUser()

    if (!auth.isAuthenticated) {
      // Preserve the destination (e.g. connect-callback links with query params)
      // so login can resume it instead of dropping the user on the dashboard.
      return to.fullPath === '/'
        ? { name: 'login' }
        : { name: 'login', query: { redirect: to.fullPath } }
    }
  }

  const requiredRoles = to.meta.roles as string[] | undefined
  if (requiredRoles && auth.user?.role && !requiredRoles.includes(auth.user.role)) {
    return { name: 'dashboard' }
  }
})

export default router
